/**
 * ActivityGuide — die Seite erklärt sich selbst, ohne die Achse zu verdrängen.
 *
 * Ein Nicht-Techniker sieht auf dieser Seite neun Kürzel und drei Bänder. Ohne
 * Vorwissen ist beides nicht lesbar — und die gefährlichste Lücke ist nicht die
 * Legende, sondern die Aktualität: „Geplant“ und „Jetzt“ sind Live-Polls,
 * „Verlauf“ ist ein Build-Artefakt. Wer das nicht weiß, wartet vergeblich
 * darauf, dass der laufende Run unten auftaucht.
 *
 * **Warum aufklappbar und standardmäßig zu.** Die Seite ist ein Betriebswerkzeug:
 * wer täglich damit arbeitet, will die Zeilen sofort sehen und liest die
 * Erklärung genau einmal. Ein dauerhafter Erklärkasten kostet jeden Tag den
 * Platz, den er einmal verdient hat. Deshalb: ein `Disclosure` (prince-ui, auf
 * React Aria — `aria-expanded`, Tastatur, Panel-Semantik sind damit erledigt),
 * zusammengedrückt auf eine Zeile Bildlaufkosten, und platziert IN der
 * Seitenkopfzeile statt zwischen Kopf und Tabelle: aufgeklappt schiebt es die
 * Achse nach unten, zugeklappt steht die Achse, wo sie vorher stand.
 *
 * **Die Slot-Legende hängt zusätzlich am Tabellenkopf** — `EXPLAINED_SLOTS`
 * setzt die Bedeutung in den `title` jedes Kürzels (siehe `stations.ts`). Ein
 * Tooltip kostet keine Fläche; wer nur „was heißt DEPL?“ wissen will, muss
 * dafür nichts aufklappen.
 *
 * **Kein `Card` drumherum.** Ein Card-Rahmen wäre zugeklappt ein zweiter
 * Kasten über dem Karten-Rahmen der Achse und würde genau die Aufmerksamkeit
 * einsammeln, die der Zeitachse gehört. Der Rahmen erscheint erst mit dem
 * geöffneten Panel.
 */
import { Fragment, type ReactNode } from "react";
import { Badge, Disclosure } from "@conuti-das/prince-ui";
import summary from "@/data/generated/summary.json";
import { STATIONS, STATION_CAVEATS } from "./stations";
import "./activity.css";

/**
 * Wie alt der Verlauf ist. Dieselbe Quelle wie der `Build:`-Stempel in
 * `src/layout/Header.tsx` (`summary.json → generated_at`) — die Erklärung darf
 * nicht auf einen anderen Zeitpunkt zeigen als die Kopfzeile, auf die sie
 * verweist.
 */
const generated = (summary as { generated_at?: string }).generated_at;
export const buildStamp = generated
  ? new Date(generated).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
  : "unbekannt";

/** Ein Band der Achse mit seiner Herkunft — die Zeile, die am meisten spart. */
interface SourceDoc {
  band: string;
  live: boolean;
  /** Woher die Daten kommen, in einem Halbsatz. */
  from: ReactNode;
  /** Was daraus folgt. */
  note: ReactNode;
}

const SOURCES: SourceDoc[] = [
  {
    band: "Geplant",
    live: true,
    from: <><code>GET /api/autoruns</code>, alle 30 s</>,
    note: "Was von selbst laufen wird — die eingerichteten Zeitpläne, nach vorn gerechnet.",
  },
  {
    band: "Jetzt",
    live: true,
    from: <><code>GET /api/queue</code>, alle 3 s</>,
    note: "Was gerade läuft und wartet. Antwortet der Runner nicht, sagt das Band es dort — die übrige Achse bleibt lesbar.",
  },
  {
    band: "Verlauf",
    live: false,
    from: (
      <>
        Laufzettel-Dateien <code>maco-meta/runs/*.json</code>, beim Bauen der Webapp über{" "}
        <code>npm run data</code> eingelesen
      </>
    ),
    note: (
      <>
        <strong>Ein Run, der gerade läuft, erscheint im Verlauf erst nach dem nächsten Build
        und Rollout.</strong> Wie aktuell die Historie ist, sagt der <code>Build:</code>-Zeitstempel
        in der Kopfzeile — hier: <strong>{buildStamp}</strong>.
      </>
    ),
  },
];

/** Wer einen Lauf in Gang setzt. Drei Fälle, mehr gibt es nicht. */
const TRIGGERS: { who: string; what: ReactNode }[] = [
  {
    who: "Ein Mensch",
    what: <>„Neuer Run“ in der Kopfzeile — oder eine einzelne Station eines Laufs neu starten.</>,
  },
  {
    who: "Ein Zeitplan",
    what: <>Ein Autorun feuert zu seiner Zeit. Er steht im Band „Geplant“, bevor er läuft.</>,
  },
  {
    who: "Ein Mensch mittendrin",
    what: (
      <>
        Eine Station steht auf <code>awaiting_user</code> und wartet auf eine Entscheidung. Der Run
        ist dann <strong>nicht kaputt</strong> — er wartet. Abbrechen lässt sich so einer deshalb
        auch nicht; er wird beantwortet.
      </>
    ),
  },
];

export function ActivityGuide() {
  return (
    <Disclosure
      className="act-guide"
      defaultExpanded={false}
      title={<span className="act-guide-trigger-text">Was zeigt diese Seite?</span>}
    >
      <div className="act-guide-body">
        <section className="act-guide-section">
          <h3 className="act-guide-head">Die neun Stationen</h3>
          <p className="act-guide-lead">
            Jede Zeile zeigt denselben Weg eines Dokuments — von der Frage, ob es beim Herausgeber
            etwas Neues gibt, bis zum scharf geschalteten Prozess. Die Kürzel stehen in der
            Kopfzeile der Tabelle; dieselbe Erklärung hängt dort auch als Tooltip.
          </p>
          {/* Drei echte Spalten in EINEM Grid statt einer Zeile je Station:
              nur so bemessen sich Kürzel- und Namensspalte am längsten Eintrag
              und fluchten trotzdem über alle neun Zeilen. Der vollständige
              Stage-Name ist der ganze Wert dieser Spalte — unter ihm taucht die
              Station im Runner und in den Laufzetteln auf, und `generate_…`
              wäre zwischen drei Stationen nicht einmal unterscheidbar. Deshalb
              `max-content` und keine gedeckelte Breite. */}
          <dl className="act-guide-stations">
            {STATIONS.map((s) => (
              <Fragment key={s.id}>
                <dt className="act-guide-key">{s.label}</dt>
                <dd className="act-guide-tech">
                  <code>{s.id}</code>
                </dd>
                <dd className="act-guide-means">{s.means}</dd>
              </Fragment>
            ))}
          </dl>
        </section>

        <section className="act-guide-section">
          <h3 className="act-guide-head">Woher die drei Bänder ihre Daten haben</h3>
          <dl className="act-guide-sources">
            {SOURCES.map((s) => (
              <div key={s.band} className="act-guide-source">
                <dt>
                  <span className="act-guide-band">{s.band}</span>
                  <Badge tone={s.live ? "green" : "gray"}>{s.live ? "live" : "nicht live"}</Badge>
                </dt>
                <dd>
                  <span className="act-guide-from">{s.from}</span>
                  <span className="act-guide-note">{s.note}</span>
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="act-guide-section">
          <h3 className="act-guide-head">Wer auslöst</h3>
          <dl className="act-guide-sources">
            {TRIGGERS.map((t) => (
              <div key={t.who} className="act-guide-source">
                <dt>
                  <span className="act-guide-band">{t.who}</span>
                </dt>
                <dd>
                  <span className="act-guide-note">{t.what}</span>
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Zwei Stationen halten nicht, was ihr Name verspricht. Das gehört in
            die Erklärung — eine Legende, die das verschweigt, erzeugt genau das
            Missverständnis, das sie ausräumen sollte. */}
        <section className="act-guide-section act-guide-caveats">
          <h3 className="act-guide-head">Zwei Einschränkungen, die man kennen muss</h3>
          <ul>
            {STATION_CAVEATS.map((s) => (
              <li key={s.id}>
                <span className="act-guide-key">{s.label}</span> {s.caveat}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Disclosure>
  );
}
